"use client";
import { useState, useEffect } from "react";
import { Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import Axios from "services/api/config";
import {
	resendFPOTPURL,
	resendOTPURL,
	verifyFPOTPURL,
	verifyOTPURL,
} from "services/api/routes/Auth";
import { toast } from "sonner";
import CustomButton from "components/common/CustomButton";
import { MuiOtpInput } from "mui-one-time-password-input";
import { TOKEN_KEYS } from "utils/constant";
import userStore from "store/userStore";

const VerifyEmailForm = () => {
	const navigate = useNavigate();
	const location: any = useLocation();
	const { setToken }: any = userStore();
	const requestId = location.state?.requestId;
	const email = location.state?.email;
	const isForgotPassword = location.state?.type === "forgot-password";
	const [otp, setOtp] = useState("");
	const [loading, setLoading] = useState(false);
	const [resendLoading, setResendLoading] = useState(false);
	const [cooldown, setCooldown] = useState(0);
	const [newRequestId, setNewRequestId] = useState("");

	useEffect(() => {
		if (!requestId) {
			toast.error("Invalid Request!");
			navigate("/");
		}
	}, [navigate, requestId]);

	useEffect(() => {
		let timer: NodeJS.Timeout;
		if (cooldown > 0) {
			timer = setInterval(() => {
				setCooldown((prev) => prev - 1);
			}, 1000);
		}
		return () => {
			if (timer) clearInterval(timer);
		};
	}, [cooldown]);

	const formatTime = (seconds: number) => {
		const minutes = Math.floor(seconds / 60);
		const remainingSeconds = seconds % 60;
		return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
	};

	const onSubmit = async (e: any) => {
		try {
			e?.preventDefault?.();
			if (loading) return;
			setLoading(true);
			const payload = {
				requestId: newRequestId || requestId,
				otp,
			};
			const { data: res }: any = await Axios({
				...(isForgotPassword ? verifyFPOTPURL : verifyOTPURL),
				data: payload,
			});
			toast.success("Verification Successful!");
			if (isForgotPassword) {
				navigate(`/set-password`, {
					state: {
						email,
						id: res.data?.requestId,
					},
				});
			} else {
				setToken({
					token: res.data.refreshToken,
					type: TOKEN_KEYS.REFRESH_TOKEN,
				});
				setToken({ token: res.data.token, type: TOKEN_KEYS.ACCESS_TOKEN });
			}
			// 	navigate
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const onResendOTP = async () => {
		try {
			if (resendLoading || cooldown > 0) return;
			setResendLoading(true);
			const { data }: any = await Axios({
				...(isForgotPassword ? resendFPOTPURL : resendOTPURL),
				data: { email },
			});
			setNewRequestId(data.data?.requestId);
			toast.success(data.message || "OTP has been resend successfully!");
			setCooldown(120); // Set 2 minutes cooldown
		} catch (error) {
			console.error(error);
		} finally {
			setResendLoading(false);
		}
	};

	return (
		<div className="w-full md:w-1/2 md:px-8 flex flex-col justify-center items-center">
			<div className="p-8 flex flex-col justify-center items-center gap-4 text-black text-center">
				<div className="text-2xl md:text-4xl uppercase font-bold font-carmine">
					Verify Email!
				</div>
				<Typography className="text-lg mb-2 text-center text-wrap">
					Please enter the One Time Password (OTP) that has been sent to your
					e-mail.
				</Typography>

				<form onSubmit={onSubmit}>
					<div className="flex mb-4">
						<MuiOtpInput
							value={otp}
							onChange={setOtp}
							// onComplete={(otp) => onSubmit({ otp })}
							// onComplete={onSubmit}
							TextFieldsProps={{ placeholder: "-" }}
							autoFocus
						/>
					</div>

					<CustomButton
						title="Verify Email"
						variant="secondary"
						type="submit"
						className="w-full"
						isLoading={loading}
						// onClick={onSendEmail}
					/>
				</form>

				{/* Login Link */}
				<p className="text-base mt-2 text-center text-black">
					Didn't receive the OTP?{" "}
					<span
						className={`${
							cooldown > 0
								? "text-gray-400"
								: "text-theme font-semibold cursor-pointer"
						}`}
						onClick={onResendOTP}
					>
						{resendLoading
							? "Loading..."
							: cooldown > 0
							? `Resend in ${formatTime(cooldown)}`
							: "Resend OTP"}
					</span>
				</p>
			</div>
		</div>
	);
};

export default VerifyEmailForm;
